/** The one date format of v2: "2 Sep 2026", or "12 Oct" where the year is implied. Times keep "9:42 AM". Every moment is read on MAC's clock (America/Detroit), whatever the viewer's machine says, so every surface names the same time. */

const ZONE = "America/Detroit";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY = new Intl.DateTimeFormat("en-US", { timeZone: ZONE, year: "numeric", month: "numeric", day: "numeric" });
const TIME = new Intl.DateTimeFormat("en-US", { timeZone: ZONE, hour: "numeric", minute: "2-digit" });

interface Day {
  year: number;
  /** 0 = January. */
  month: number;
  day: number;
}

/** The calendar day a moment falls on in Detroit. */
function dayAt(date: Date): Day {
  const part = (type: string) => Number(DAY.formatToParts(date).find((p) => p.type === type)?.value);
  return { year: part("year"), month: part("month") - 1, day: part("day") };
}

/** A date-only ISO string is a calendar day (never shifted by a time zone); a datetime is a moment. */
function parse(iso: string | null | undefined): { day: Day; at: Date | null } | null {
  if (!iso) return null;
  const only = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (only) return { day: { year: Number(only[1]), month: Number(only[2]) - 1, day: Number(only[3]) }, at: null };
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : { day: dayAt(at), at };
}

const short = ({ day, month }: Day): string => `${day} ${MONTHS[month]}`;

/** "12 Oct 2026"; "—" for nothing. */
export function fmtDate(iso: string | null | undefined): string {
  const read = parse(iso);
  return read ? `${short(read.day)} ${read.day.year}` : "—";
}

/** "12 Oct"; "—" for nothing. */
export function shortDate(iso: string | null | undefined): string {
  const read = parse(iso);
  return read ? short(read.day) : "—";
}

/** "9:42 AM" today, "11 Oct, 9:42 AM" otherwise (a date-only value has no time: "11 Oct"); null for a missing or unreadable time. */
export function fmtWhen(iso: string | null | undefined): string | null {
  const read = parse(iso);
  if (!read) return null;
  if (!read.at) return short(read.day);
  const time = TIME.format(read.at);
  const today = dayAt(new Date());
  const same = read.day.year === today.year && read.day.month === today.month && read.day.day === today.day;
  return same ? time : `${short(read.day)}, ${time}`;
}

/** Whole days from today (MAC's) to a date-only ISO day: 0 today, negative once past; null for nothing. */
export function daysFromToday(iso: string | null | undefined, now: Date = new Date()): number | null {
  const read = parse(iso);
  if (!read) return null;
  const today = dayAt(now);
  const utc = (d: Day) => Date.UTC(d.year, d.month, d.day);
  return Math.round((utc(read.day) - utc(today)) / 86_400_000);
}
